<template>
  <v-col class="py-0">
    <v-row>
      <v-col class="py-0" cols="12">
        <v-text-field
          v-model="data.name"
          :label="$t('form.address.fields.name')"
          :placeholder="getPlaceholder(_.get(country, 'code'), 'name')"
        />
      </v-col>
    </v-row>

    <v-row
      v-for="(r, i) in addressFormatted"
      :key="`row-${i}`"
    >
      <v-col
        v-for="(c, j) in r"
        :key="`col-${j}`"
        class="py-0"
      >
        <v-text-field
          v-if="_.includes(['address1', 'address2', 'city', 'zip'], c)"
          v-model="data[c]"
          :label="_.get(country, `labels.${c}`, '')"
          :placeholder="getPlaceholder(_.get(country, 'code'), c)"
        />

        <v-autocomplete
          v-else-if="c === 'country'"
          v-model="data.country"
          :label="_.get(country, 'labels.country', '')"
          :placeholder="getPlaceholder(_.get(country, 'code'), 'country')"
          :items="countries"
          item-text="name"
          item-value="name"
        />

        <v-autocomplete
          v-else-if="c === 'province'"
          v-model="data.region"
          :label="_.get(country, 'labels.zone', '')"
          :disabled="_.isEmpty(regions)"
          :items="regions"
          :placeholder="getPlaceholder(_.get(country, 'code'), 'region')"
          item-text="name"
          item-value="code"
        />
      </v-col>
    </v-row>

    <slot />
  </v-col>
</template>

<script>
import AddressFormatter from '@shopify/address';
import { mapGetters } from 'vuex';
import EditFormMixin from '../../mixins/editForm';
import TypesMixin from '../../mixins/types';
import DropdownValuesMixin from '../../mixins/dropdownValues';

export default {
  mixins: [
    EditFormMixin,
    TypesMixin,
    DropdownValuesMixin,
  ],

  props: {
    addressType: String,
  },

  data() {
    return {
      fields: 'form.address.fields',

      placeholder: 'form.address.placeholders',

      unfilteredCountries: [],
    };
  },

  computed: {
    ...mapGetters('countries', {
      findCountries: 'find',
    }),

    ...mapGetters('regions', {
      findRegions: 'find',
    }),

    fetchedCountries() {
      return _.get(this.findCountries({ query: {} }), 'data', []);
    },

    fetchedRegions() {
      return _.get(this.findRegions({ query: {} }), 'data', []);
    },

    regions() {
      return _.get(this, 'country.zones', [])
        .filter(r => this.fetchedRegions.map(fr => fr.Code).includes(r.code));
    },

    country() {
      return _.find(this.countries,
        c => _.toLower(c.name) === _.toLower(_.get(this.data, 'country')));
    },

    countries() {
      return this.unfilteredCountries.filter(c => this.fetchedCountries.map(fc => fc.Code).includes(c.code));
    },

    addressFormatter() {
      return new AddressFormatter('CA');
    },

    addressFormatted() {
      const DEFAULT_FORM_LAYOUT = '{firstName}{lastName}_{company}_{address1}'
        + '_{address2}_{city}_{country}{province}{zip}_{phone}';

      // https://regex101.com/r/SH6ovw/1
      const formatString = _.get(this, 'country.formatting.edit', DEFAULT_FORM_LAYOUT)
        .replace('({firstName}(_?)|{lastName}(_?)|{company}(_?))', '');

      let rows = formatString.split('_');
      rows = _.map(rows, r => r.split('}{'));

      rows = _.each(rows, r => {
        r[0] = _.first(r).replace('{', '');
        r[r.length - 1] = _.last(r).replace('}', '');
      });

      return rows;
    },
  },

  mounted() {
    this.getUnfilteredCountries();
  },

  methods: {
    async getUnfilteredCountries() {
      const r = await this.addressFormatter.getCountries();
      this.unfilteredCountries = r;
    },

    getPlaceholder(countryCode, fieldName) {
      const key = `${this.placeholder}.${this.addressType}.${countryCode}.${fieldName}`;

      if (this.$te(key)) {
        return this.$t(key);
      }

      return this.$t(`${this.placeholder}.${this.addressType}.default.${fieldName}`);
    }
  },
};
</script>
